// File: frontend/src/components/LegalPageLayout.tsx
// Shared visual scaffolding for the legal pages (Terms of Service, Privacy
// Policy) so they follow the cyber design system like the rest of the app.
import React from 'react';
import { Box, Chip, Paper, Typography, alpha } from '@mui/material';
import { cyber } from '../theme';

export const LegalPageHeader: React.FC<{
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    lastUpdated: string;
}> = ({ icon, title, subtitle, lastUpdated }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box
            sx={{
                width: 56,
                height: 56,
                borderRadius: 2.5,
                background: cyber.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 4px 16px rgba(123, 76, 255, 0.35)',
                flexShrink: 0,
            }}
        >
            {icon}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 200 }}>
            <Typography variant="h4" component="h1" sx={{ lineHeight: 1.2 }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {subtitle}
            </Typography>
        </Box>
        <Chip
            label={`Last Updated: ${lastUpdated}`}
            size="small"
            sx={{
                fontFamily: cyber.fontMono,
                fontSize: '0.7rem',
                color: cyber.accent.cyan,
                background: alpha(cyber.accent.cyan, 0.12),
                border: `1px solid ${alpha(cyber.accent.cyan, 0.4)}`,
            }}
        />
    </Box>
);

export const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
        variant="h5"
        component="h2"
        gutterBottom
        sx={{
            mt: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            '&::before': {
                content: '""',
                display: 'block',
                width: 4,
                height: 22,
                borderRadius: 2,
                background: cyber.gradient,
                flexShrink: 0,
            },
        }}
    >
        {children}
    </Typography>
);

export const NoticeBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Paper
        elevation={0}
        sx={{
            p: 2,
            my: 3,
            bgcolor: alpha(cyber.primary, 0.06),
            border: `1px solid ${alpha(cyber.primary, 0.25)}`,
            borderLeft: `3px solid ${cyber.primary}`,
            borderRadius: 2,
        }}
    >
        {children}
    </Paper>
);
